import { PlatformLocation } from '@angular/common';
import { Component, Inject, Optional } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { BaseComponent } from '../../../shared/base-components/base.component';
import { getServerDate, setServerDateToString } from '../../../shared/helpers/time-helpers.model';
import { CustomerIndividualBiometric } from '../../models/customer-individual-biometric.model';
import { IndividualDetailViewModel } from '../../models/views/individual-detail-view.model';
import { CustomerManageService } from '../../shared/customer-manage.service';
import { CustomerStatusType } from '../../../Foundation/models/enums/customer-status-type.enum';
import { AuthService } from '../../../auth/services/auth.service';
import { forkJoin } from 'rxjs';
import { deepClone } from '../../../shared/helpers/clone-helpers';

export class CustomerDetailsContext {
    constructor(public customerNumberId: number) {}
}

@Component({
    selector: 'individual-customer-details-modal',
    templateUrl: './individual-customer-details-modal.html'
})

export class CustomerDetailsModalComponent extends BaseComponent {
    public customerNumberId: number;
    public individualDetails: IndividualDetailViewModel = new IndividualDetailViewModel();
    public ssn: string;
    public maxDate: Date = new Date();
    public showButtonLoading = false;
    public detailsForm: UntypedFormGroup;
    public canEdit = false;
    public isBiometricRedacted = true;
    public originalStatus: number;

    private canEditDeceasedDate = false;
    private customerStatusTypes: CustomerStatusType = new CustomerStatusType();
    private CUSTOMER_IDENTIFICATION_TYPE_SSN = 5;

    constructor(
        public location: PlatformLocation,
        public fb: UntypedFormBuilder,
        public service: CustomerManageService,
        public dialogRef: MatDialogRef<CustomerDetailsModalComponent>,
        authService: AuthService,
        @Optional() @Inject(MAT_DIALOG_DATA) public data: CustomerDetailsContext
    ) {
        super();

        this.canEditDeceasedDate = authService.hasACSTransactionClaimEx(authService.acsTransactionClaim, 'CanEditDeceasedDate');
        this.canEdit = authService.hasACSTransactionClaimEx(authService.acsTransactionClaim, 'UpdCustomerActiveIndicator');

        location.onPopState(() => {
            this.dialogRef.close();
        });
    }

    public ngOnInit(): void {
        this.detailsForm = this.fb.group({
            'dateOfBirth': [''],
            'deceasedDate': [''],
            'doNotUse': [null]
        });

        if (!this.canEditDeceasedDate) {
            this.detailsForm.get('deceasedDate').disable();
        }

        this.customerNumberId = this.data.customerNumberId;
        this.getDetails(this.customerNumberId);
    }

    /**
     * Gets all necessary customer info
     * @param customerNumber an identifier for the customer
     **/
    private getDetails(customerNumber: number): void {
        this.service.getIndividual(customerNumber.toString())
            .subscribe(result => {
                const individualDetails: IndividualDetailViewModel = result;
                const doNotUse = individualDetails.statusTypeId == this.customerStatusTypes.Closed.id;
                this.detailsForm.get('doNotUse').setValue(doNotUse);
                this.originalStatus = individualDetails.statusTypeId;
                const ssnIdentification = individualDetails.identifications.find(x => x.customerIdentificationTypeId == this.CUSTOMER_IDENTIFICATION_TYPE_SSN);
                if (ssnIdentification) {
                    this.ssn = ssnIdentification.customerIdentificationValue;
                }

                // biometric data
                const biometric: CustomerIndividualBiometric = result.biometric ? result.biometric : new CustomerIndividualBiometric();
                const deceasedDate = getServerDate(biometric, 'deceasedDate');
                this.detailsForm.get('deceasedDate').setValue(deceasedDate);
                this.isBiometricRedacted = biometric.isBiometricRedacted;
                if (!this.isBiometricRedacted) {
                    const dob = getServerDate(biometric, 'dateOfBirth');
                    this.detailsForm.get('dateOfBirth').setValue(dob);
                } else {
                    this.detailsForm.get('dateOfBirth').disable();
                }

                individualDetails.biometric = biometric;
                this.individualDetails = individualDetails;
            },
            e => this.handleValidationResult(e));
    }

    public cancel(): void {
        this.dialogRef.close(this.originalStatus);
    }

    /**
     * Saves the customer information in its current state
     **/
    public saveDetails(): void {
        if (this.detailsForm.valid) {
            this.showButtonLoading = true;
            const model = this.getModel();

            forkJoin({
                saveCustomerAttributes: this.service.saveCustomerAttributes(model.biometric),
                statusUpdate : this.service.updateStatus(this.customerNumberId, model.statusTypeId)
            }).subscribe(
                result => {
                    this.showButtonLoading = false;
                    this.dialogRef.close(model.statusTypeId);
                },
                error => {
                    this.showButtonLoading = false;
                    this.handleValidationResult(error);
                }
            );
        }
    }

    private getModel(): IndividualDetailViewModel {
        const model = deepClone(this.individualDetails);

        const deceasedDate = this.detailsForm.get('deceasedDate').value;
        model.biometric.deceasedDate = setServerDateToString(deceasedDate);
        model.statusTypeId = this.detailsForm.get('doNotUse').value ? this.customerStatusTypes.Closed.id : this.customerStatusTypes.Active.id;

        // If the dob must be redacted, then the dob field that the user sees will have no data.
        // If a "save" request is made while that field has no data, then no changes will be saved for the dob.
        if (this.detailsForm.get('dateOfBirth').value) {
            const dateOfBirth = this.detailsForm.get('dateOfBirth').value;
            model.biometric.dateOfBirth = setServerDateToString(dateOfBirth);
        }

        return model;
    }
}
